import type { ObjectType } from "../hooks/useObjectType";
import { deriveRootAreaType, getAreaLogic } from "./area-logic";

// Minimal shape shared by the web and native Firestore snapshots.
export type AreaDoc = {
  id: string;
  get: (field: string) => any;
};

export type AreaRow = {
  kind: "area";
  id: string;
  area: AreaDoc;
  parentId: string | null;
  level: number;
  rootAreaType: string;
  hasChildren: boolean;
  isExpanded: boolean;
};

export type AddAreaRow = {
  kind: "addArea";
  id: string;
  parentId: string | null;
  level: number;
  rootAreaType?: string;
};

export type Row = AreaRow | AddAreaRow;

export const INDENT_WIDTH = 14;

const childrenOf = (areas: AreaDoc[], parentId: string | null) =>
  areas.filter((a) => (a.get("parentId") ?? null) === parentId);

/**
 * Turns the area tree into the flat list of rows the web capture view renders.
 *
 * Rendering flat (rather than recursively nesting a SortableContext per level)
 * is what lets a drag express a new parent: position gives the order, the
 * horizontal offset gives the level, and both are read off this one array.
 */
export const flattenAreas = ({
  areas,
  objectType,
  isExpanded,
  skipSubtreeOf,
}: {
  areas: AreaDoc[];
  objectType: ObjectType | null;
  isExpanded: (id: string) => boolean;
  // While dragging, the moved area's descendants are left out entirely, so they
  // can neither be rendered in two places nor be dropped into.
  skipSubtreeOf?: string;
}): Row[] => {
  const rows: Row[] = [];

  const walk = (
    currentAreas: AreaDoc[],
    level: number,
    inheritedRootAreaType: string | undefined
  ) => {
    for (const area of currentAreas) {
      const children = childrenOf(areas, area.id);
      const rootAreaType =
        inheritedRootAreaType ?? deriveRootAreaType(area.get("title"));
      const expanded = isExpanded(area.id);

      rows.push({
        kind: "area",
        id: area.id,
        area,
        parentId: area.get("parentId") ?? null,
        level,
        rootAreaType,
        hasChildren: children.length > 0,
        isExpanded: expanded,
      });

      if (skipSubtreeOf === area.id) {
        continue;
      }

      if (expanded) {
        walk(children, level + 1, rootAreaType);

        const { canAddSubareas } = getAreaLogic(objectType, level, rootAreaType);

        if (canAddSubareas) {
          rows.push({
            kind: "addArea",
            id: `add-${area.id}`,
            parentId: area.id,
            level: level + 1,
            rootAreaType,
          });
        }
      }
    }
  };

  walk(childrenOf(areas, null), 1, undefined);

  const couldAddRootAreas = !(objectType === "street" && areas.length > 0);

  if (couldAddRootAreas) {
    rows.push({ kind: "addArea", id: "add-root", parentId: null, level: 1 });
  }

  return rows;
};

export const isAreaRow = (row: Row): row is AreaRow => row.kind === "area";

export type SubtreeNode = {
  id: string;
  relativeDepth: number;
  hasChildren: boolean;
  hasCracks: boolean;
};

/** The moved area plus every descendant, with depths relative to the area. */
export const getSubtree = (
  areas: AreaDoc[],
  rootId: string,
  hasCracks: (areaId: string) => boolean = () => false
): SubtreeNode[] => {
  const result: SubtreeNode[] = [];

  const walk = (id: string, relativeDepth: number) => {
    const children = childrenOf(areas, id);

    result.push({
      id,
      relativeDepth,
      hasChildren: children.length > 0,
      hasCracks: hasCracks(id),
    });

    for (const child of children) {
      walk(child.id, relativeDepth + 1);
    }
  };

  walk(rootId, 0);

  return result;
};

export type Projection = {
  parentId: string | null;
  level: number;
  /** Index the moved area takes among its new siblings. */
  index: number;
};

const arrayMove = <T,>(items: T[], from: number, to: number): T[] => {
  const next = items.slice();
  next.splice(to < 0 ? next.length + to : to, 0, next.splice(from, 1)[0]);
  return next;
};

/**
 * Works out where a drag would actually put the area: the vertical position
 * picks the slot, the horizontal offset picks the level, and every level that
 * useAreaLogic rejects for the moved subtree is discarded before we settle on
 * one. Returns null when no legal level is reachable from this slot, which is
 * the signal to show no drop indicator and to ignore the drop.
 */
export const getProjection = ({
  items,
  activeId,
  overId,
  dragOffset,
  objectType,
  subtree,
  indentWidth = INDENT_WIDTH,
}: {
  items: AreaRow[];
  activeId: string;
  overId: string;
  dragOffset: number;
  objectType: ObjectType | null;
  subtree: SubtreeNode[];
  indentWidth?: number;
}): Projection | null => {
  const overIndex = items.findIndex((i) => i.id === overId);
  const activeIndex = items.findIndex((i) => i.id === activeId);

  if (overIndex < 0 || activeIndex < 0) {
    return null;
  }

  const active = items[activeIndex];
  const newItems = arrayMove(items, activeIndex, overIndex);
  const previous = newItems[overIndex - 1];
  const next = newItems[overIndex + 1];

  const projectedLevel = active.level + Math.round(dragOffset / indentWidth);
  const maxLevel = previous ? previous.level + 1 : 1;
  const minLevel = next ? next.level : 1;

  const parentIdForLevel = (level: number): string | null => {
    if (level === 1 || !previous) {
      return null;
    }

    if (level > previous.level) {
      return previous.id;
    }

    if (level === previous.level) {
      return previous.parentId;
    }

    const ancestor = newItems
      .slice(0, overIndex)
      .reverse()
      .find((i) => i.level === level);

    return ancestor?.parentId ?? null;
  };

  const byId = new Map(items.map((i) => [i.id, i]));

  const isLegal = (level: number, parentId: string | null): boolean => {
    const parent = parentId ? byId.get(parentId) : undefined;

    if (parentId && !parent) {
      return false;
    }

    // Crossing between Aussen and Innen changes which rule set applies, so the
    // type is resolved for the new position rather than carried over.
    const rootAreaType = parent
      ? parent.rootAreaType
      : deriveRootAreaType(active.area.get("title"));

    if (parent) {
      const { canAddSubareas } = getAreaLogic(
        objectType,
        parent.level,
        parent.rootAreaType
      );

      if (!canAddSubareas) {
        return false;
      }
    }

    // A pure reorder leaves every level and rule set in the subtree exactly as
    // it was, so it stays allowed even for areas that already sit at a level
    // the current rules would not produce.
    if (level === active.level && rootAreaType === active.rootAreaType) {
      return true;
    }

    // Otherwise the whole moved subtree shifts with the area, so a level is
    // only legal when every descendant still fits at its new depth.
    return subtree.every(({ relativeDepth, hasChildren, hasCracks }) => {
      const logic = getAreaLogic(
        objectType,
        level + relativeDepth,
        rootAreaType
      );

      if (hasChildren && !logic.canAddSubareas) {
        return false;
      }

      if (hasCracks && !logic.canAddCracks) {
        return false;
      }

      return true;
    });
  };

  const candidates: number[] = [];
  for (let level = minLevel; level <= maxLevel; level++) {
    candidates.push(level);
  }

  const best = candidates
    .map((level) => ({ level, parentId: parentIdForLevel(level) }))
    .filter(({ level, parentId }) => isLegal(level, parentId))
    .sort(
      (a, b) =>
        Math.abs(a.level - projectedLevel) - Math.abs(b.level - projectedLevel) ||
        b.level - a.level
    )[0];

  if (!best) {
    return null;
  }

  const index = newItems
    .slice(0, overIndex)
    .filter((i) => i.id !== activeId && i.parentId === best.parentId).length;

  return { parentId: best.parentId, level: best.level, index };
};
